import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];
  #inertedPageElements = [];
  #viewportRafId = null;
  #onViewportChange = () => {
    if (!this.isOpen || this.#viewportRafId !== null) return;

    this.#viewportRafId = requestAnimationFrame(() => {
      this.#viewportRafId = null;
      if (this.isOpen) this.#syncViewportOffset();
    });
  };
  #headerResizeObserver = new ResizeObserver(this.#onViewportChange);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    window.addEventListener('resize', this.#onViewportChange);
    window.visualViewport?.addEventListener('resize', this.#onViewportChange);
    document.addEventListener('scroll', this.#onViewportChange, { passive: true });
    const header = this.closest('header-component') ?? document.getElementById('header-component');
    if (header instanceof HTMLElement) this.#headerResizeObserver.observe(header);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    window.removeEventListener('resize', this.#onViewportChange);
    window.visualViewport?.removeEventListener('resize', this.#onViewportChange);
    document.removeEventListener('scroll', this.#onViewportChange);
    this.#headerResizeObserver.disconnect();
    if (this.#viewportRafId !== null) {
      cancelAnimationFrame(this.#viewportRafId);
      this.#viewportRafId = null;
    }
    this.#setPageInert(false);
    removeTrapFocus();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    const details = this.#getDetailsElement(event);
    this.#close(details.open ? details : this.refs.details);
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');
    const isMainDrawer = details === this.refs.details;

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    if (isMainDrawer) {
      this.#syncViewportOffset();
      this.#setPageInert(true);
    }

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      if (isMainDrawer) this.#syncViewportOffset();
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
        this.#setMainCloseInert(true);
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = isMainDrawer
        ? this.refs.menuDrawer
        : details.querySelector(':scope > .menu-drawer__submenu') || details.querySelector('.menu-drawer__submenu');
      const initialFocus = isMainDrawer
        ? this.refs.menuDrawer.querySelector('.menu-drawer__close-button')
        : drawer?.querySelector('.menu-drawer__back-button, input, button, a[href]');
      onAnimationEnd(drawer || details, () => trapFocus(drawer || details, initialFocus), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   * @returns {Promise<void>}
   */
  back(event) {
    return this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   * @returns {Promise<void>}
   */
  close() {
    return this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   * @returns {Promise<void>}
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return Promise.resolve();

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
    this.#setMainCloseInert(false);

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    return onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          this.#setPageInert(false);
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
          summary.focus({ preventScroll: true });
        } else {
          trapFocus(this.refs.menuDrawer, summary);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Keep page content outside the modal drawer out of keyboard and assistive-technology navigation.
   * @param {boolean} isInert
   */
  #setPageInert(isInert) {
    if (isInert) {
      if (this.#inertedPageElements.length) return;

      this.#inertedPageElements = [document.getElementById('MainContent'), document.getElementById('gullyo-footer-group')]
        .filter((element) => element instanceof HTMLElement && !element.inert);

      this.#inertedPageElements.forEach((element) => {
        element.inert = true;
        element.setAttribute('data-gullyo-menu-inert', '');
      });
      return;
    }

    this.#inertedPageElements.forEach((element) => {
      if (element.getAttribute('data-gullyo-menu-inert') !== null) {
        element.inert = false;
        element.removeAttribute('data-gullyo-menu-inert');
      }
    });
    this.#inertedPageElements = [];
  }

  /**
   * Remove the main drawer close button from interaction while a submenu owns focus.
   * @param {boolean} isInert
   */
  #setMainCloseInert(isInert) {
    const mainClose = this.refs.menuDrawer.querySelector(':scope > .menu-drawer__close-button');
    if (!(mainClose instanceof HTMLButtonElement)) return;

    mainClose.inert = isInert;
    if (isInert) {
      mainClose.setAttribute('aria-hidden', 'true');
    } else {
      mainClose.removeAttribute('aria-hidden');
    }
  }

  /**
   * Keep the drawer flush with the currently visible bottom edge of the sticky header.
   */
  #syncViewportOffset() {
    const header = this.closest('header-component') ?? document.getElementById('header-component');
    const visibleHeaderBottoms =
      header instanceof HTMLElement
        ? Array.from(header.querySelectorAll('.header, .header__row, .gullyo-announcement-section'))
            .filter((element) => element instanceof HTMLElement && element.getClientRects().length > 0)
            .map((element) => element.getBoundingClientRect().bottom)
        : [];
    const headerBottom = Math.max(
      header instanceof HTMLElement ? header.getBoundingClientRect().bottom : 0,
      ...visibleHeaderBottoms
    );
    const offset = Math.max(0, Math.min(window.innerHeight, Math.round(headerBottom)));

    this.style.setProperty('--gullyo-menu-drawer-top', `${offset}px`);
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
